import { deviceRegistry } from '@/devices/catalog';
import type { DeviceRegistry } from '@/devices/registry';
import { DEVICE_CATEGORIES } from '@/devices/types';
import type { TranslationKey } from '@/i18n';
import { useT } from '@/i18n/useT';
import { Badge } from '@/ui/components/Badge';
import { EmptyState } from '@/ui/components/EmptyState';
import { Panel, PanelBody, PanelHeader } from '@/ui/components/Panel';
import { Tooltip } from '@/ui/components/Tooltip';
import { Icon } from '@/ui/icons/Icon';
import { useProjectStore } from '@/store/projectStore';
import { useUiStore } from '@/store/uiStore';
import { useSimulationStore } from '@/store/simulationStore';
import type { Device } from '@/devices/types';
import type { DeviceId } from '@/core/types';
import styles from './ComponentsPanel.module.css';

/**
 * Component toolbox. It lists whatever the device registry contains, so implementing a device in
 * a later phase makes it appear here without touching this panel. In Phase 1 every entry is
 * `'planned'` and therefore disabled.
 */
export function ComponentsPanel({ registry = deviceRegistry }: { registry?: DeviceRegistry }) {
  const t = useT();
  const devices = useProjectStore((s) => s.project.scene.devices);
  const setDevices = useProjectStore((s) => s.setDevices);
  const select = useUiStore((s) => s.select);
  const isDesignMode = useUiStore((s) => s.activeMode === 'design');
  const isSimRunning = useSimulationStore((s) => s.snapshot.state !== 'STOPPED');
  const hasDevices = registry.list().length > 0;

  return (
    <Panel 
      label={t('components.label')} 
      className={styles.panel}
      style={isSimRunning ? { opacity: 0.6, pointerEvents: 'none', userSelect: 'none' } : undefined}
    >
      <PanelHeader title={t('components.title')} icon="gauge" />
      <PanelBody>
        {hasDevices ? (
          DEVICE_CATEGORIES.map((category) => {
            const definitions = registry.list(category);
            if (definitions.length === 0) return null;
            return (
              <div key={category} className={styles.category}>
                <h3 className={styles.categoryTitle}>{t(`components.category.${category}` as TranslationKey)}</h3>
                <ul className={styles.list}>
                  {definitions.map((definition) => (
                    <li key={definition.type}>
                      <Tooltip placement="right" content={t(definition.meta.descriptionKey)}>
                        <button
                          type="button"
                          className={styles.item}
                          disabled={definition.availability !== 'available' || isSimRunning}
                          draggable={definition.availability === 'available' && !isSimRunning}
                          onDragStart={(e) => {
                            if (isSimRunning) {
                              e.preventDefault();
                              return;
                            }
                            e.dataTransfer.setData('application/x-plc-device', definition.type);
                          }}
                          onClick={() => {
                            if (!isDesignMode || isSimRunning || definition.availability !== 'available') return;
                            const newDevice: Device = {
                              id: `${definition.type}-${Date.now()}` as DeviceId,
                              type: definition.type,
                              name: `New ${definition.type.charAt(0).toUpperCase() + definition.type.slice(1)}`,
                              transform: {
                                position: {
                                  x: 120 + (devices.length % 6) * 40,
                                  y: 120 + (devices.length % 6) * 40,
                                },
                                rotation: 0,
                              },
                              props: { ...definition.defaultProps },
                            };
                            setDevices([...devices, newDevice]);
                            select(newDevice.id, definition.type);
                          }}
                        >
                          <Icon name={definition.meta.icon} size={20} />
                          <span className={styles.itemName}>{t(definition.meta.nameKey)}</span>
                          {definition.availability === 'planned' && (
                            <Badge>{t('components.soon')}</Badge>
                          )}
                        </button>
                      </Tooltip>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })
        ) : (
          <EmptyState
            title={t('components.empty.title')}
            description={t('components.empty.description')}
          />
        )}
      </PanelBody>
    </Panel>
  );
}
